import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import useMediaQuery from "@shared/hooks/useMediaQuery/useMediaQuery";
import breakpoints from "@shared/theme/props/breakpoints";
import Icon from "@ui-kit/icons/Icon.web";
import Flex, { type FlexProps } from "@ui-kit/layout/Flex/Flex.web";
import React from "react";
import SwiperCore, { A11y, Mousewheel, Navigation, Virtual } from "swiper";
import { Swiper as BaseSwiper, SwiperSlide } from "swiper/react";

SwiperCore.use([Navigation, Mousewheel, A11y, Virtual]);

// CSS for Swiper's internal classes that we can't target with props
const swiperContainerCss = {
  "& .swiper-container": {
    maxWidth: "100% !important",
    width: "100% !important",
    height: "auto",
  },
  "& .swiper-button-prev": {
    color: "var(--clr-font-dark)",
  },
  "& .swiper-button-next": {
    color: "var(--clr-font-dark)",
  },
  "& .swiper-slide": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "fit-content !important",
  },
  "& .swiper-slide img": {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
};

const Swiper = ({
  children,
  swiperContainerProps,
  slidesOffsetBefore,
}: {
  children: React.ReactNode;
  swiperContainerProps?: FlexProps;
  slidesOffsetBefore?: number;
}) => {
  const [isSmallerThanMd] = useMediaQuery(`(max-width: ${breakpoints.md}px)`);
  // Generate unique ID and replace colons with dashes
  const uniqueId = React.useId().replace(/:/g, "-");
  const prevClass = `swiper-prev-${uniqueId}`;
  const nextClass = `swiper-next-${uniqueId}`;

  const offSetBefore = isSmallerThanMd ? 20 : 0;
  const slidesOffsetBeforeValue = slidesOffsetBefore
    ? slidesOffsetBefore
    : offSetBefore;

  return (
    <Flex
      height="auto"
      alignItems="center"
      width="100%"
      zIndex={0}
      // eslint-disable-next-line showpass-custom/prefer-semantic-props
      css={swiperContainerCss}
      {...swiperContainerProps}
    >
      <Flex direction="column" w="100%" gap="md">
        {/* negative margin on top to bring these buttons in line with heading (desktop only) */}
        <Flex
          direction="row"
          justifyContent="right"
          gap="2xs"
          mt={{ base: "none", md: "-34px" }}
          h={{ base: "none", md: "auto" }}
        >
          <IconButton.Primitive
            aria-label="Previous products"
            variant="unstyled"
            className={prevClass}
            bg="transparent"
            _focus={{ boxShadow: "none" }}
          >
            <Icon.ChevronLeftRounded
              color="gray.700"
              display={{ base: "none", md: "block" }}
            />
          </IconButton.Primitive>

          <IconButton.Primitive
            aria-label="Next products"
            variant="unstyled"
            className={nextClass}
            bg="transparent"
            _focus={{ boxShadow: "none" }}
          >
            <Icon.ChevronRightRounded
              color="gray.700"
              display={{ base: "none", md: "block" }}
            />
          </IconButton.Primitive>
        </Flex>
        <BaseSwiper
          // force swiper to re-render when slidesOffsetBefore changes
          key={slidesOffsetBeforeValue}
          freeMode
          mousewheel={{
            forceToAxis: true, // Restricts scrolling to a single axis
            sensitivity: 1, // Sets the sensitivity of mouse wheel scrolling
          }}
          navigation={{
            prevEl: `.${prevClass}`,
            nextEl: `.${nextClass}`,
          }}
          spaceBetween={20}
          slidesOffsetAfter={20}
          slidesOffsetBefore={slidesOffsetBeforeValue}
          slidesPerView="auto"
        >
          {children}
        </BaseSwiper>
      </Flex>
    </Flex>
  );
};

export default Swiper;

Swiper.Slide = SwiperSlide;
